import React, { useEffect, useState, useMemo } from "react";

import {
  Ticket,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  Calendar,
  Percent,
  DollarSign,
  Ban,
  CheckCircle,
  ChevronDown,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import TextInputComp from "../../../components/customer/TextInputComp";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import { CreateCouponRequest } from "../../../types/Request/AdminRequest"; // Adjust type paths
import {
  useCreateCouponMutation,
  useGetCouponListMutation,
  useLazyDeleteCouponQuery,
  useLazyGetCouponByIdQuery,
  useUpdateCouponMutation,
} from "../../../redux/actions/Admin/ProductAdmin";
import { ICoupon } from "../../../types/Response/AdminResponse";

const CouponManagement: React.FC = () => {
  // List Pagination & Filtering States
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Form Modal Interface States
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);

  // RTK Query Binding Definitions
  const [fetchCouponList, { data: couponResponse, isLoading: isLoadingList }] =
    useGetCouponListMutation();
  const [createCoupon, { isLoading: isCreating }] = useCreateCouponMutation();
  const [updateCoupon, { isLoading: isUpdating }] = useUpdateCouponMutation();
  const [triggerGetById, { isFetching: isFetchingSingle }] =
    useLazyGetCouponByIdQuery();
  const [triggerDeleteCoupon, { isFetching: isDeleting }] =
    useLazyDeleteCouponQuery();

  const discountTypeOptions: Option[] = [
    { id: "1", label: "Percentage (%)", value: "2" },
    { id: "2", label: "Fixed Amount (AED)", value: "1" },
    { id: "3", label: "Free Product", value: "3" },
    { id: "4", label: "Free Shipping", value: "4" },
  ];

  // Form Data State Binding Structure
  const [selectedDiscountType, setSelectedDiscountType] =
    useState<Option | null>(discountTypeOptions[0]);
  const [form, setForm] = useState({
    code: "",
    discountValue: 0,
    minOrderAmount: 0,
    maximumDiscount: 0,
    usageLimit: 500,
    expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0], // Default 30 days ahead YYYY-MM-DD
    isActive: true,
  });

  const isProcessing =
    isLoadingList || isCreating || isUpdating || isFetchingSingle || isDeleting;

  // Load and Synchronize data tables roster logs grid indices
  const refreshCouponRosterList = () => {
    fetchCouponList({
      search: searchQuery.trim() || undefined,
      pageNumber: currentPage,
      pageSize: pageSize,
    });
  };

  useEffect(() => {
    refreshCouponRosterList();
  }, [currentPage, searchQuery, fetchCouponList]);

  const handleOpenCreateMode = () => {
    setEditingCouponId(null);
    setSelectedDiscountType(discountTypeOptions[0]);
    setForm({
      code: "",
      discountValue: 0,
      minOrderAmount: 0,
      maximumDiscount: 0,
      usageLimit: 500,
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      isActive: true,
    });
    setIsFormModalOpen(true);
  };

  const handleOpenEditMode = async (coupon: ICoupon) => {
    if (!coupon.id) return;
    setEditingCouponId(coupon.id);

    try {
      // Unpack comprehensive payload variables values straight from remote query fetcher
      const res = await triggerGetById(coupon.id).unwrap();
      const targetCoupon = res;

      setForm({
        code: targetCoupon.code,
        discountValue: targetCoupon.discountValue,
        minOrderAmount: targetCoupon.minOrderAmount,
        maximumDiscount: targetCoupon.maximumDiscount,
        usageLimit: targetCoupon.usageLimit,
        expiryDate: targetCoupon.expiryDate?.split("T")[0] || "",
        isActive: targetCoupon.isActive,
      });

      const matchedType = discountTypeOptions.find(
        (o) =>
          o.value?.toLowerCase() === targetCoupon.discountType?.toLowerCase(),
      );
      setSelectedDiscountType(matchedType || discountTypeOptions[0]);
      setIsFormModalOpen(true);
    } catch (err) {
      console.error("Failed to recover target data metrics payload", err);
    }
  };

  const handleDeleteClickAction = async (id: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this promotional coupon code template entry permanently?",
      )
    )
      return;
    try {
      await triggerDeleteCoupon(id).unwrap();
      refreshCouponRosterList();
    } catch (err) {
      console.error(
        "Failed to execute deletion tracking task log parameters:",
        err,
      );
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !selectedDiscountType || isProcessing) return;

    try {
      if (editingCouponId) {
        const payload: CreateCouponRequest = {
          id: editingCouponId,
          code: form.code.trim().toUpperCase(),
          discountType: selectedDiscountType.value,
          discountValue: Number(form.discountValue) || 0,
          minOrderAmount: Number(form.minOrderAmount) || 0,
          maximumDiscount: Number(form.maximumDiscount) || 0,
          usageLimit: Number(form.usageLimit) || 0,
         expiryDate: new Date(form.expiryDate).toISOString(),
          isActive: form.isActive,
        };
        await updateCoupon(payload).unwrap();
      } else {
        const payload: CreateCouponRequest = {
          code: form.code.trim().toUpperCase(),
          discountType: selectedDiscountType.value,
          discountValue: Number(form.discountValue) || 0,
          minOrderAmount: Number(form.minOrderAmount) || 0,
          maximumDiscount: Number(form.maximumDiscount) || 0,
          usageLimit: Number(form.usageLimit) || 0,
         expiryDate: new Date(form.expiryDate).toISOString(),
          isActive: form.isActive,
        };
        await createCoupon(payload).unwrap();
      }
      setIsFormModalOpen(false);
      refreshCouponRosterList();
    } catch (err) {
      console.error("Coupon transactional modification collapsed:", err);
    }
  };

  const couponsDataList = couponResponse?.data ?? [];
  const totalRecordsCount = couponResponse?.totalRecords ?? 0;
  const totalPagesCount = Math.ceil(totalRecordsCount / pageSize) || 1;

  return (
    <div className="p-4 sm:p-6 lg:p-8 min-h-screen text-slate-800 text-left max-w-7xl mx-auto w-full relative select-none">
      {/* GLOBAL LOADING GLASS OVERLAY PROCESSING TRACKER */}
      {isProcessing && !isFormModalOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-3xs z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-5 flex items-center gap-3 shadow-2xl border border-slate-100">
            <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
            <span className="text-xs font-bold text-slate-700">
              Processing balance parameters registry modifications...
            </span>
          </div>
        </div>
      )}

      {/* TOP BANNER TITLE SECTION */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-5 gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <Ticket className="text-[#2F5E2E] w-5 h-5 stroke-[2.5]" />
            Vouchers & Coupons Ledger
          </h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">
            Formulate campaign referral vouchers discount models, enforce
            validation thresholds limits, or trace active live inventory codes.
          </p>
        </div>

        <CommonButton
          icon={<Plus size={15} />}
          iconPosition="left"
          text="Create Promotional Code"
          onClick={handleOpenCreateMode}
          variant="filled"
          className="bg-[#2F5E2E] text-white font-black text-xs  h-10 shadow-3xs rounded-xl hover:bg-[#1f3f1e] cursor-pointer"
        />
      </div>

      {/* SEARCH CRITERIA INPUT CARD ELEMENT */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-3xs mb-6 max-w-md relative flex items-center">
        <input
          type="text"
          placeholder="Search voucher code metrics..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full pl-10 pr-4 py-2 bg-slate-50/50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-[#6DBE45] bg-white transition"
        />
        <Search className="absolute left-7 text-slate-400 w-4 h-4" />
      </div>

      {/* --- LISTING CANVAS RENDERING TABLE CONTAINER --- */}
      {isLoadingList ? (
        <div className="py-20 text-center flex flex-col items-center justify-center gap-2 text-xs font-semibold text-slate-400 italic">
          <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
          <span>Loading coupon matrices from main cluster registry...</span>
        </div>
      ) : couponsDataList.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-16 text-center text-xs font-black text-slate-400 italic">
          No matching discount coupon configurations saved inside data maps yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop View Table */}
          <div className="hidden md:block rounded-2xl border border-slate-200 bg-white shadow-3xs overflow-hidden">
            <table className="w-full text-left border-collapse table-fixed">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70 text-slate-400 text-[11px] font-black tracking-wider uppercase">
                  <th className="p-4 w-[160px]">Voucher Code</th>
                  <th className="p-4 w-[140px]">Discount Value</th>
                  <th className="p-4 w-[140px]">Min Basket Target</th>
                  <th className="p-4 w-[120px]">Usage Balance</th>
                  <th className="p-4 w-[120px]">Expiration Limit</th>
                  <th className="p-4 w-[110px] text-center">Status</th>
                  <th className="p-4 w-[100px] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-bold text-slate-700">
                {couponsDataList.map((coupon) => (
                  <tr
                    key={coupon.id}
                    className="hover:bg-slate-50/50 align-middle"
                  >
                    <td className="p-4">
                      <span className="font-mono font-black text-slate-900 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg text-xs tracking-wider">
                        {coupon.code}
                      </span>
                    </td>
                    <td className="p-4 text-slate-900 text-sm">
                      {coupon.discountType === "Percentage" ? (
                        <span className="flex items-center gap-0.5 text-blue-700 font-black">
                          <Percent size={13} /> {coupon.discountValue}% Off
                        </span>
                      ) : (
                        <span className="font-mono font-black text-slate-900">
                          AED {coupon.discountValue}
                        </span>
                      )}
                    </td>
                    <td className="p-4 font-mono text-slate-500">
                      AED {coupon.minOrderAmount}
                    </td>
                    <td className="p-4 text-slate-600 font-medium">
                      Limit:{" "}
                      <span className="font-bold text-slate-800">
                        {coupon.usageLimit}
                      </span>
                    </td>
                    <td className="p-4 text-slate-500 font-mono text-[11px]">
                      {coupon.expiryDate
                        ? coupon.expiryDate.split("T")[0]
                        : "Never"}
                    </td>
                    <td className="p-4 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black border ${coupon.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-400 border-slate-100"}`}
                      >
                        {coupon.isActive ? "ACTIVE" : "PAUSED"}
                      </span>
                    </td>
                    <td className="p-4 text-right flex items-center justify-end gap-2.5 pt-4">
                      <button
                        onClick={() => handleOpenEditMode(coupon)}
                        className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-[#2F5E2E] hover:bg-white cursor-pointer shadow-3xs transition"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() =>
                          coupon.id && handleDeleteClickAction(coupon.id)
                        }
                        className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-white cursor-pointer shadow-3xs transition"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Layout Fallback View */}
          <div className="block md:hidden space-y-4 w-full">
            {couponsDataList.map((coupon) => (
              <div
                key={coupon.id}
                className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3.5 shadow-3xs text-xs font-bold"
              >
                <div className="flex justify-between items-center border-b border-slate-100 pb-2.5">
                  <span className="font-mono font-black text-slate-900 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-lg tracking-wider">
                    {coupon.code}
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-black border ${coupon.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-50 text-slate-400 border-slate-100"}`}
                  >
                    {coupon.isActive ? "ACTIVE" : "PAUSED"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-500 font-medium">
                  <div>
                    Discount:{" "}
                    <span className="font-black text-slate-900 block mt-0.5">
                      {coupon.discountType === "Percentage"
                        ? `${coupon.discountValue}%`
                        : `AED ${coupon.discountValue}`}
                    </span>
                  </div>
                  <div>
                    Expiry:{" "}
                    <span className="font-mono text-slate-700 block mt-0.5">
                      {coupon.expiryDate
                        ? coupon.expiryDate.split("T")[0]
                        : "Never"}
                    </span>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => handleOpenEditMode(coupon)}
                    className="text-slate-500 hover:text-[#2F5E2E] px-2 py-1 border border-slate-200 rounded-lg bg-slate-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      coupon.id && handleDeleteClickAction(coupon.id)
                    }
                    className="text-rose-500 hover:text-rose-700 px-2 py-1 border border-slate-200 rounded-lg bg-slate-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* PAGINATION INTERACTIVE CONTROLS BAR ROW */}
          {totalPagesCount > 1 && (
            <div className="flex justify-between items-center pt-2 font-bold text-xs text-slate-400">
              <span>
                Showing record entries range block of {totalRecordsCount}{" "}
                results matrix.
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className="px-3 h-8 border border-slate-200 bg-white rounded-xl text-slate-700 cursor-pointer disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="bg-slate-100 px-3 py-1.5 rounded-lg text-slate-800 font-mono font-black">
                  {currentPage} / {totalPagesCount}
                </span>
                <button
                  disabled={currentPage === totalPagesCount}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPagesCount))
                  }
                  className="px-3 h-8 border border-slate-200 bg-white rounded-xl text-slate-700 cursor-pointer disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==========================================================
                UNIFIED TRANSACTIONS OVERLAY MODAL FORM (ADD NEW & EDIT MODES)
                ========================================================== */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg shadow-2xl flex flex-col max-h-[92vh] overflow-hidden text-left relative">
            {isProcessing && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-3xs z-30 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
                <span className="text-xs font-black text-slate-700">
                  Writing changes parameters payload down ledger indices...
                </span>
              </div>
            )}

            <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black text-slate-900 tracking-tight">
                  {editingCouponId
                    ? "Modify Voucher Parameters"
                    : "Formulate Promotional Coupon Code"}
                </h3>
                <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase mt-0.5">
                  Enforcing cluster rules limitations values constraints
                </p>
              </div>
              <button
                onClick={() => setIsFormModalOpen(false)}
                className="p-1.5 rounded-xl border border-transparent hover:border-slate-200 hover:bg-white text-slate-400 hover:text-slate-700 cursor-pointer transition"
              >
                <X size={15} />
              </button>
            </div>

            <form
              onSubmit={handleFormSubmit}
              className="p-5 overflow-y-auto space-y-4 text-xs font-semibold text-slate-700"
            >
              <TextInputComp
                showLabel
                label="Unique Voucher Coupon Code"
                showStar
                placeholder="EX: PETLANDSUMMER20"
                value={form.code}
                onChangeText={(txt) => setForm({ ...form, code: txt })}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Dropdown
                  options={discountTypeOptions}
                  value={selectedDiscountType}
                  onChange={(opt) => setSelectedDiscountType(opt)}
                  showLabel
                  label="Discount Strategy Form"
                  innerClassName="bg-slate-50 border-slate-200 font-bold py-2"
                  dropdownIcon={<ChevronDown size={14} />}
                />
                <TextInputComp
                  showLabel
                  label="Discount Rate / Value"
                  showStar
                  placeholder="20"
                  type="number"
                  value={String(form.discountValue || "")}
                  onChangeText={(txt) =>
                    setForm({ ...form, discountValue: Number(txt) || 0 })
                  }
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInputComp
                  showLabel
                  label="Minimum Basket Value Target (AED)"
                  showStar
                  placeholder="150"
                  type="number"
                  value={String(form.minOrderAmount || "")}
                  onChangeText={(txt) =>
                    setForm({ ...form, minOrderAmount: Number(txt) || 0 })
                  }
                />
                <TextInputComp
                  showLabel
                  label="Maximum Ceiling Cap Limit (AED)"
                  showStar
                  placeholder="50"
                  type="number"
                  value={String(form.maximumDiscount || "")}
                  onChangeText={(txt) =>
                    setForm({ ...form, maximumDiscount: Number(txt) || 0 })
                  }
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInputComp
                  showLabel
                  label="Global Total Usage Headcount Limit"
                  showStar
                  placeholder="1000"
                  type="number"
                  value={String(form.usageLimit || "")}
                  onChangeText={(txt) =>
                    setForm({ ...form, usageLimit: Number(txt) || 0 })
                  }
                />
                <TextInputComp
                  showLabel
                  label="Voucher Expiration Date Limit"
                  showStar
                  type="date"
                  placeholder="2026-07-30"
                  value={form.expiryDate}
                  onChangeText={(txt) => setForm({ ...form, expiryDate: txt })}
                />
              </div>

              {/* Status Option Box Toggle Row */}
              <div className="flex items-center select-none bg-[#FAFBF8] border p-3 rounded-xl border-dashed border-slate-200 w-fit cursor-pointer">
                <input
                  id="isCouponActiveToggleInput"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                  className="w-4 h-4 text-[#2F5E2E] border-gray-300 rounded focus:ring-[#6DBE45] cursor-pointer"
                />
                <label
                  htmlFor="isCouponActiveToggleInput"
                  className="ml-2.5 text-xs font-bold text-slate-700 cursor-pointer flex items-center gap-1"
                >
                  <span>
                    Enforce visibility rules immediately (Activate voucher on
                    save)
                  </span>
                </label>
              </div>

              {/* Footer triggers form buttons buttons execution links rows */}
              <div className="flex justify-end gap-2.5 pt-4 border-t border-slate-100">
                <CommonButton
                  text="Discard changes"
                  variant="outline"
                  className="px-4 h-10 text-xs border-slate-200"
                  onClick={() => setIsFormModalOpen(false)}
                  disabled={isProcessing}
                />
                <CommonButton
                  text={
                    editingCouponId
                      ? "Apply Coupon Modifications"
                      : "Deploy Coupon Live"
                  }
                  variant="filled"
                  disabled={
                    isProcessing || !form.code.trim() || !form.discountValue
                  }
                  className="bg-[#2F5E2E] hover:bg-[#1f3f1e] px-4 h-10 font-black shadow-sm text-white"
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
